import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  toast,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeletePlotPortfolioProduct } from '@/api/rest/resources/plotPortfolioProducts';
import { PlotPortfolioProduct } from '@/api/rest/resources/types/plotPortfolioProducts';
import { PLOT_PORTFOLIO_PRODUCTS_QUERY_KEY } from '@/hooks/usePlotPortfolioProducts';

type DeletePlotPortfolioProductDialogProps = {
  plotPortfolioProduct: PlotPortfolioProduct | null;
  onClose: () => void;
};

export const DeletePlotPortfolioProductDialog = ({
  plotPortfolioProduct,
  onClose,
}: DeletePlotPortfolioProductDialogProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deleteMutation = useDeletePlotPortfolioProduct();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!plotPortfolioProduct) return;

    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync({ pathVariables: { id: plotPortfolioProduct.id } });

      queryClient.invalidateQueries({ queryKey: [PLOT_PORTFOLIO_PRODUCTS_QUERY_KEY] });
      toast({ type: 'success', title: t('shared.ndiProducts.toast.deleteSuccess') });
      onClose();
    } catch {
      toast({ type: 'error', title: t('shared.ndiProducts.toast.deleteError') });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={!!plotPortfolioProduct} onOpenChange={onClose}>
      <DialogContent data-testid='delete-plot-portfolio-product-dialog'>
        <DialogHeader className='typography-h2 w-full text-error md:typography-h3 sm:text-center'>
          {t('shared.ndiProducts.deleteDialog.header')}
        </DialogHeader>
        <DialogBody className='mb-8'>
          {t('shared.ndiProducts.deleteDialog.body', { name: plotPortfolioProduct?.display_name })}
        </DialogBody>
        <DialogFooter className='justify-end'>
          <DialogClose asChild>
            <Button variant='text'>{t('global.ui.buttons.cancel')}</Button>
          </DialogClose>
          <Button onClick={handleDelete} loading={isDeleting} className='bg-error hover:bg-error-dark'>
            {t('global.ui.buttons.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
